<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import {
  createTransmissionPeakHour,
  deleteTransmissionPeakHour,
  getTransmissionPeakHours,
} from '@/services/cpm/cpm.service'
import { getISOOptions, getLRZOptionsForISO, getTPZOptionsForISO, ISOs } from '@/types/core/Site'
import type { ITransmissionPeakHourFilters } from '@/types/cpm/filters'
import type { ITransmissionPeakHourForm } from '@/types/cpm/forms'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { formatJSDateToDateString } from '@/util/formatters'
import { DateTime } from 'luxon'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { FormInstance, FormRules } from 'element-plus'
import { ElMessage } from 'element-plus'
import { range, last } from 'lodash'
import { computed, reactive, ref, watch } from 'vue'

// UI buffers
const createAlertDialogVisible = ref(false)
const moreFiltersVisible = ref(false)

// Form ref
const addPeakHourFormRef = ref<FormInstance>()

// Filters
const filters = reactive<ITransmissionPeakHourFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  iso: undefined,
  load_resource_zone: undefined,
  transmission_pricing_zone: undefined,
  year: new Date().getFullYear(),
  month: undefined,
  hour_ending: undefined,
})

// Buffer for the TPZ cascader
const tpzPath = ref<string[]>()

// Options for addTransmissionPeakHour creation form
const ISOFormOptions = getISOOptions()
const LRZFormOptions = computed(() => (addPeakHourForm.iso ? getLRZOptionsForISO(addPeakHourForm.iso) : undefined))
const TPZFormOptions = computed(() => (addPeakHourForm.iso ? getTPZOptionsForISO(addPeakHourForm.iso) : undefined))
const hourFilterOptions = range(24).map((i) => ({ label: i + 1, value: i + 1 }))
const monthFilterOptions = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
].map((month, index) => ({ label: month, value: index + 1 }))

// Form and rules

interface AddPeakHourForm {
  date: Date
  hour_ending: number | null
  iso: ISOs | null
  load_resource_zone: string | null
  transmission_pricing_zone: string[] | null
}

const addPeakHourForm = reactive<AddPeakHourForm>({
  date: new Date(),
  hour_ending: null,
  iso: null,
  load_resource_zone: null,
  transmission_pricing_zone: null,
})

const addPeakHourFormRules = reactive<FormRules>({
  date: [{ required: true, message: 'Please select a date', trigger: ['blur', 'change'] }],
  hour_ending: [{ required: true, message: 'Please select an hour', trigger: ['blur', 'change'] }],
  iso: [{ required: true, message: 'Please select an ISO', trigger: ['blur', 'change'] }],
  load_resource_zone: [{ required: true, message: 'Please select a LRZ', trigger: ['blur', 'change'] }],
  transmission_pricing_zone: [{ required: true, message: 'Please select a TPZ', trigger: ['blur', 'change'] }],
})

const isFormInvalid = computed(() => {
  return (
    !addPeakHourForm.date ||
    !addPeakHourForm.hour_ending ||
    !addPeakHourForm.iso ||
    !addPeakHourForm.transmission_pricing_zone
  )
})

// Handlers

const queryClient = useQueryClient()

const { mutateAsync: deletePeakHour } = useMutation({
  mutationFn: (index: string) => deleteTransmissionPeakHour(index),
  onSuccess: async () => {
    queryClient.invalidateQueries({ queryKey: ['transmission-peak-hours'] })
    ElMessage({
      message: 'Peak Hour deleted successfully.',
      type: 'success',
    })
  },
  onError: (error: unknown) => {
    if (error instanceof Error)
      ElMessage({
        message: error.message,
        type: 'error',
      })
  },
})

const { mutateAsync: createPeakHour } = useMutation({
  mutationFn: (payload: ITransmissionPeakHourForm) => createTransmissionPeakHour(payload),
  onSuccess: async () => {
    queryClient.invalidateQueries({ queryKey: ['transmission-peak-hours'] })
    ElMessage({
      message: 'Peak Hour added successfully.',
      type: 'success',
    })
  },
  onError: (error: unknown) => {
    if (error instanceof Error)
      ElMessage({
        message: error.message,
        type: 'error',
      })
  },
})

const openAddPeakHourDialog = (formEl: FormInstance | undefined) => {
  addPeakHourForm.date = new Date()
  addPeakHourForm.hour_ending = null
  addPeakHourForm.iso = null
  addPeakHourForm.load_resource_zone = null
  addPeakHourForm.transmission_pricing_zone = null
  formEl?.resetFields()
  createAlertDialogVisible.value = true
}

const handleAddPeakHour = async (formEl: FormInstance | undefined) => {
  if (!formEl) return

  await formEl.validate(async (valid) => {
    const { iso, transmission_pricing_zone, date, hour_ending, load_resource_zone } = addPeakHourForm
    // Narrow type check
    if (!valid || !iso || !transmission_pricing_zone || !hour_ending) return

    createAlertDialogVisible.value = false

    const payload: ITransmissionPeakHourForm = {
      iso,
      load_resource_zone,
      transmission_pricing_zone: last(transmission_pricing_zone)!,
      hour_ending: hour_ending.toString(),
      date: formatJSDateToDateString(date),
      timezone: 'US/Central',
    }

    await createPeakHour(payload)
  })
}

const { data, refetch, isPending, error, isRefetching } = useQuery({
  queryKey: ['transmission-peak-hours', filters],
  queryFn: () => getTransmissionPeakHours(filters),
})

// Filter options

const ISOFilterOptions = getISOOptions()
const LRZFilterOptions = computed(() => (filters.iso ? getLRZOptionsForISO(filters.iso) : undefined))
const TPZFilterOptions = computed(() => (filters.iso ? getTPZOptionsForISO(filters.iso) : undefined))

const yearDateSelected = ref(new Date())
watch(yearDateSelected, (newDate: Date) => {
  filters.year = newDate ? newDate.getFullYear() : undefined
})

const tableLoadingText = computed(() => {
  if (isPending.value) {
    return 'Loading...'
  }
  return 'No data.'
})

const tableData = computed(() => {
  if (!data.value?.data) return []
  return data.value.data.map((item) => {
    const dtCentral = DateTime.fromISO(item.datetime_utc, { zone: 'utc' }).setZone('US/Central')
    return {
      ...item,
      period: dtCentral.toFormat('MMMM yyyy'),
      date: dtCentral.toFormat('MM/dd/yyyy'),
      hour_ending: dtCentral.hour + 1,
    }
  })
})
</script>

<template>
  <ViewLayout class="transmission-peak">
    <template #controls>
      <el-button type="primary" :disabled="!!error" @click="openAddPeakHourDialog(addPeakHourFormRef)">
        Add Peak Hour
      </el-button>
    </template>

    <!-- Error state -->
    <rl-error-result v-if="error && !isRefetching" :error="error">
      <el-button @click="refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>

    <!-- Creation dialog -->
    <el-dialog v-model="createAlertDialogVisible" class="w-max rounded-lg" title="Add Peak Hour" :show-close="false">
      <el-form
        ref="addPeakHourFormRef"
        :model="addPeakHourForm"
        :rules="addPeakHourFormRules"
        label-position="left"
        label-width="190px"
        class="flex flex-col justify-around gap-4 w-[600px]"
        hide-required-asterisk
      >
        <el-form-item label="Date" prop="date">
          <el-date-picker v-model="addPeakHourForm.date" class="w-56" placeholder="Select date" />
        </el-form-item>
        <el-form-item label="Hour Ending" prop="hour_ending">
          <el-select-v2
            v-model="addPeakHourForm.hour_ending"
            class="w-55"
            placeholder="Select hour"
            placement="bottom"
            :options="hourFilterOptions"
          />
        </el-form-item>

        <el-form-item label="ISO" prop="iso">
          <el-select-v2
            v-model="addPeakHourForm.iso"
            class="w-128"
            placeholder="Select ISO"
            placement="bottom"
            :options="ISOFormOptions"
          />
        </el-form-item>

        <el-form-item label="Load Resource Zone" prop="load_resource_zone">
          <el-select-v2
            v-model="addPeakHourForm.load_resource_zone"
            class="w-128"
            :placeholder="!addPeakHourForm.iso || LRZFormOptions ? 'Select LRZ' : 'No LRZs available for selected ISO'"
            placement="bottom"
            :options="LRZFormOptions ?? []"
            :disabled="!LRZFormOptions"
          />
        </el-form-item>

        <el-form-item label="Tranmission Pricing Zone" prop="transmission_pricing_zone">
          <el-cascader
            v-model="addPeakHourForm.transmission_pricing_zone"
            class="w-128"
            :placeholder="!addPeakHourForm.iso || TPZFormOptions ? 'Select TPZ' : 'No TPZs available for selected ISO'"
            placement="bottom"
            :show-all-levels="false"
            :options="TPZFormOptions"
            :disabled="!TPZFormOptions"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="createAlertDialogVisible = false">Cancel</el-button>
          <el-button type="primary" :disabled="isFormInvalid" @click="handleAddPeakHour(addPeakHourFormRef)">
            Save
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- Data loaded, display the filters -->
    <template v-if="!isPending">
      <!-- Filters -->
      <div class="flex gap-2">
        <el-select-v2
          v-model="filters.month"
          class="w-52"
          placeholder="Month"
          filterable
          clearable
          :options="monthFilterOptions"
        />
        <el-date-picker
          v-model="yearDateSelected"
          type="year"
          class="w-52"
          placeholder="Year"
          clearable
          filterable
          :disabled-date="(date: Date) => date > new Date()"
        />
        <el-select-v2
          v-model="filters.hour_ending"
          class="w-52"
          placeholder="Hour Ending"
          filterable
          clearable
          :options="hourFilterOptions"
        />

        <el-popover
          placement="bottom-start"
          width="420"
          trigger="click"
          persistent
          :visible="moreFiltersVisible"
          @before-leave="() => false"
        >
          <template #reference>
            <el-button @click="moreFiltersVisible = !moreFiltersVisible">More Filters</el-button>
          </template>

          <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-1">
              <span class="inline-block min-w-[100px] text-right mr-2 text-xs text-text-tertiary">ISO</span>
              <el-select-v2
                v-model="filters.iso"
                class="w-full"
                placeholder="ISO"
                clearable
                filterable
                :options="ISOFilterOptions"
                @clear="
                  () => {
                    // Reset dependent filters
                    filters.load_resource_zone = undefined
                    filters.transmission_pricing_zone = undefined
                  }
                "
              />
            </div>

            <!-- LRZs: disabled if no LRZs are found for selected ISO -->
            <div class="flex flex-col gap-1">
              <span class="inline-block min-w-[100px] text-right mr-2 text-xs text-text-tertiary"
                >Load Resource Zone</span
              >
              <el-select-v2
                v-model="filters.load_resource_zone"
                class="w-full"
                clearable
                filterable
                :placeholder="!filters.iso || LRZFilterOptions ? 'LRZ' : 'No LRZs available for selected ISO'"
                :options="LRZFilterOptions ?? []"
                :disabled="!LRZFilterOptions"
              />
            </div>

            <!-- TPZs: disabled if no LRZs are found for selected ISO -->
            <div class="flex flex-col gap-1">
              <span class="inline-block min-w-[100px] text-right mr-2 text-xs text-text-tertiary"
                >Transmission Pricing Zone</span
              >
              <el-cascader
                v-model="tpzPath"
                class="w-full"
                :show-all-levels="false"
                clearable
                filterable
                :placeholder="!filters.iso || TPZFilterOptions ? 'TPZ' : 'No TPZs available for selected ISO'"
                :options="TPZFilterOptions"
                :disabled="!TPZFilterOptions"
                @change="
                  (value: any) => {
                    filters.transmission_pricing_zone = value ? last(value as string[]) : undefined
                  }
                "
              />
            </div>
          </div>
        </el-popover>
      </div>
    </template>

    <!-- Data table -->
    <el-table v-loading="isPending" :data="tableData" :empty-text="tableLoadingText">
      <el-table-column prop="iso" label="ISO" />
      <el-table-column prop="load_resource_zone" label="Local Resource Zone (LRZ)" />
      <el-table-column prop="transmission_pricing_zone" label="Transmission Pricing Zone (TPZ)" min-width="200" />
      <el-table-column prop="period" label="Period" />
      <el-table-column prop="date" label="Date" />
      <el-table-column prop="hour_ending" label="Hour Ending" />
      <el-table-column width="100px">
        <template #default="scope">
          <el-button
            link
            size="small"
            title="Delete"
            @click="
              DeleteConfirmationModal(
                'Delete Transmission Peak',
                'Are you sure you want to delete this transmission peak? This action cannot be undone',
                scope.row.id,
                deletePeakHour
              )
            "
          >
            <Icon icon="material-symbols:delete-outline-rounded" inline height="20" />
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Pagination -->
    <el-pagination
      v-if="data && data?.total_pages > 0"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      background
      :layout="DEFAULT_PAGINATION_LAYOUT"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="data?.total_pages"
      :total="data?.total_records"
    />
  </ViewLayout>
</template>

<style scoped lang="scss">
:deep() {
  .el-pagination {
    align-self: flex-end;
  }
}
</style>
